// Sign in and sign up with a Google account. The button only exists when the API has Google configured
// (`authOptionsQuery`). Signing in never creates an account: that is what the sign-up screen asks for, once
// its privacy box is ticked (`requestSignUp`), so an account is never made behind the terms. Google leaves
// the page for its own (a full navigation), and comes back to the callback of the API, which sends the
// person to where they were going, or here again with `?error=` when something went wrong.
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authOptionsQuery } from './api';
import { authClient } from './auth-client';
import { FormError } from './forms';
import { Button } from './ui';
import { googleProblem } from './util';

/** The four colours of Google's mark; it is decoration, the button's name is its text. */
function GoogleMark() {
  return (
    <svg aria-hidden="true" focusable="false" width="18" height="18" viewBox="0 0 48 48">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/** The divider between the form and the Google button. */
function Or() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 text-sm text-ink-muted" role="presentation">
      <span className="h-px flex-1 bg-line" />
      <span>{t('google.or')}</span>
      <span className="h-px flex-1 bg-line" />
    </div>
  );
}

/**
 * The Google button, with its divider, above or under the form of the sign-in or sign-up screen. Nothing is drawn
 * when Google is not configured. On the sign-up screen `ready` says whether the privacy box is ticked: if
 * it is not, the button does not leave and `onNotReady` brings the person to the box.
 */
export function GoogleSignIn({
  mode,
  target,
  ready = true,
  onNotReady,
  placement = 'below',
}: {
  mode: 'login' | 'signup';
  /** The page the person was going to; they land there after Google. */
  target: string | undefined;
  ready?: boolean;
  onNotReady?: () => void;
  /** Under the form (the divider goes before the button) or above it (the divider goes after). */
  placement?: 'above' | 'below';
}) {
  const { t, i18n } = useTranslation();
  const available = useQuery(authOptionsQuery).data?.google === true;
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Coming back with the browser's Back button restores this page as it was left, with the button busy.
  useEffect(() => {
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) setBusy(false);
    };
    window.addEventListener('pageshow', restored);
    return () => window.removeEventListener('pageshow', restored);
  }, []);

  if (!available) return null;

  async function start() {
    if (!ready) {
      onNotReady?.();
      return;
    }
    setFailed(false);
    setBusy(true);
    try {
      const back = target ? `?redirect=${encodeURIComponent(target)}` : '';
      const { error } = await authClient.signIn.social({
        provider: 'google',
        callbackURL: target ?? '/',
        errorCallbackURL: `/${mode}${back}`,
        // Only the sign-up screen may make an account; the language is the one this screen is in.
        requestSignUp: mode === 'signup' ? true : undefined,
        additionalData: { locale: i18n.language },
      });
      // Without an error the browser is already on its way to Google: the button stays busy until it goes.
      if (error) throw new Error(error.code);
    } catch {
      setBusy(false);
      setFailed(true);
    }
  }

  return (
    <>
      {placement === 'below' && <Or />}
      <Button variant="secondary" type="button" isPending={busy} onPress={start}>
        <GoogleMark />
        {busy ? t('google.connecting') : t(mode === 'signup' ? 'google.signup' : 'google.login')}
      </Button>
      <span role="status" className="sr-only">
        {busy ? t('google.connecting') : ''}
      </span>
      {failed && (
        <p role="alert" className="-mt-2 text-sm font-bold text-error">
          {t('google.errors.failed')}
        </p>
      )}
      {placement === 'above' && <Or />}
    </>
  );
}

/** What went wrong on the way back from Google, as the alert of the sign-in screen (it takes the focus). */
export function GoogleProblem({
  code,
  redirect,
}: {
  code: string | undefined;
  redirect: string | undefined;
}) {
  const { t } = useTranslation();
  const problem = googleProblem(code);
  if (!problem) return null;
  const message = {
    cancelled: t('google.errors.cancelled'),
    noAccount: t('google.errors.noAccount'),
    notLinked: t('google.errors.notLinked'),
    notVerified: t('google.errors.notVerified'),
    failed: t('google.errors.failed'),
  }[problem];
  return (
    <FormError message={message} attempt={0}>
      {problem === 'noAccount' && (
        <p className="mt-2">
          <Link to="/signup" search={{ redirect }}>
            {t('login.signup')}
          </Link>
        </p>
      )}
    </FormError>
  );
}
